import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";

import { cn } from "../lib/utils";
import { Spinner } from "./spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

const buttonVariants = cva(
  "relative inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[box-shadow,background-color,border-color,color,filter] duration-200 ease-out disabled:pointer-events-none disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary gradient-raised-color text-primary-foreground border border-border-hairline shadow-raised hover:bg-primary/90 active:shadow-pressed active:brightness-95",
        flat: "pointer-coarse:min-h-11 bg-foreground text-background border border-transparent hover:bg-foreground/90 active:bg-foreground/80",
        "flat-outline":
          "pointer-coarse:min-h-11 bg-transparent text-foreground border border-border hover:bg-surface-hover active:bg-accent-muted",
        outline:
          "bg-transparent text-foreground border border-border shadow-raised-soft hover:bg-surface-hover active:shadow-pressed [&_svg]:text-muted-foreground",
        secondary:
          "bg-surface-raised gradient-raised text-foreground border border-border-hairline shadow-raised hover:bg-accent-muted active:shadow-pressed active:brightness-95",
        ghost:
          "bg-transparent text-foreground border border-accent shadow-raised-soft hover:bg-accent-muted active:bg-accent-muted active:shadow-pressed",
        link: "link",
        nav: "text-foreground hover:bg-surface-hover",
        quiet:
          "pointer-coarse:min-h-11 pointer-coarse:min-w-11 bg-transparent text-muted-foreground hover:bg-surface-hover active:bg-accent-muted motion-reduce:transition-none",
        "destructive-quiet":
          "pointer-coarse:min-h-11 pointer-coarse:min-w-11 bg-transparent text-destructive-text hover:bg-destructive-muted active:bg-destructive-muted motion-reduce:transition-none",
        destructive:
          "bg-destructive gradient-raised-color text-destructive-foreground border border-border-hairline shadow-raised hover:bg-destructive/90 active:shadow-pressed active:brightness-95",
      },
      size: {
        default: "h-9 px-4 py-2",
        xs: "h-7 rounded-md gap-1 px-3",
        sm: "h-8 rounded-md gap-1.5 px-3",
        lg: "h-10 rounded-md px-6",
        icon: "size-9 [&_svg]:text-foreground",
        "icon-xs":
          "size-6 rounded-sm [&_svg]:text-foreground [&_svg:not([class*='size-'])]:size-3.5",
        "icon-sm": "size-8 [&_svg]:text-foreground",
        "icon-lg": "size-10 [&_svg]:text-foreground",
      },
      isLoading: {
        true: "text-transparent",
      },
    },
    compoundVariants: [
      {
        variant: ["quiet", "destructive-quiet"],
        // Keep icon sizes from overriding the chosen quiet color. Loading
        // content inherits transparent; the spinner has its own color.
        className: "[&_svg]:text-inherit",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      isLoading: false,
    },
  },
);

const loadingIndicatorVariants = cva(
  "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-5 animate-spin",
  {
    variants: {
      variant: {
        default: "text-primary-foreground",
        flat: "text-background",
        "flat-outline": "text-foreground",
        outline: "text-foreground",
        secondary: "text-foreground",
        ghost: "text-foreground",
        link: "text-primary",
        nav: "text-secondary-foreground",
        quiet: "text-muted-foreground!",
        "destructive-quiet": "text-destructive-text!",
        destructive: "text-destructive-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function preserveLinkRole(render: ButtonPrimitive.Props["render"]) {
  if (!isValidElement(render)) {
    return render;
  }

  const renderProps = render.props as { href?: unknown; role?: unknown };
  if (renderProps.href == null || renderProps.role != null) {
    return render;
  }

  return cloneElement(render as ReactElement<Record<string, unknown>>, {
    role: "link",
  });
}

function Button({
  className,
  variant = "default",
  size = "default",
  disabled = false,
  isLoading = false,
  disabledReason,
  id,
  onClick,
  render,
  nativeButton,
  type,
  children,
  ...props
}: ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    /**
     * Why the button can't be used right now. Shown as a tooltip while
     * `disabled` is true; ignored otherwise, so callers pass the reason
     * unconditionally and let `disabled` gate it.
     *
     * This is THE way to explain a disabled button. Don't wrap a disabled
     * button in a tooltip trigger `<div />` instead: a natively disabled
     * button swallows pointer events (which is what tempts the wrapper),
     * the wrapper's box is whatever the surrounding layout hands it (so the
     * bubble drifts away from the button when that layout is block-level
     * rather than a flex row), and keyboard users can't reach the reason at
     * all.
     *
     * An explained button is not natively disabled. It carries
     * `aria-disabled`, keeps its place in the tab order, and has its action
     * stripped (`onClick` dropped, `type` forced to "button" so it can
     * never submit a form) — the WAI-ARIA pattern for a control that is
     * unavailable but still worth explaining. That is what lets the button
     * itself be the tooltip trigger, anchored by construction, and it is
     * why the tooltip is in informational mode: with no action left, a tap
     * is free to mean "show me why", so touch devices (no hover, and iOS
     * doesn't focus a tapped button) can reach the reason too. Base UI's
     * `focusableWhenDisabled` is NOT the tool here: it keeps a disabled
     * button focusable but still swallows every click and key handler, the
     * tooltip trigger's included.
     *
     * While `isLoading`, the button is natively disabled and unexplained:
     * loading is its own, transient, reason.
     */
    disabledReason?: ReactNode;
  }) {
  const renderedElement =
    nativeButton === false ? preserveLinkRole(render) : render;
  const explained =
    disabled && !isLoading && disabledReason != null && disabledReason !== "";

  const content = render ? (
    children
  ) : (
    <>
      {children}
      {isLoading && (
        <Spinner className={cn(loadingIndicatorVariants({ variant }))} />
      )}
    </>
  );
  const primitiveProps: ButtonPrimitive.Props = {
    id,
    className: cn(buttonVariants({ variant, size, isLoading, className })),
    nativeButton,
    render: renderedElement,
    ...(explained
      ? {
          "aria-disabled": true,
          type: render ? type : "button",
        }
      : {
          disabled: isLoading || disabled,
          onClick,
          type: render ? type : (type ?? "submit"),
        }),
    ...props,
  };
  const dataProps = {
    "data-slot": "button",
    "data-variant": variant,
    "data-size": size,
  };

  if (!explained) {
    return (
      <ButtonPrimitive {...dataProps} {...primitiveProps}>
        {content}
      </ButtonPrimitive>
    );
  }
  return (
    <Tooltip informational>
      {/* The trigger stamps its own `id` over the rendered element's, so the
          caller's id has to travel on the trigger to survive. */}
      <TooltipTrigger
        id={id}
        nativeButton={nativeButton !== false}
        render={<ButtonPrimitive {...dataProps} {...primitiveProps} />}
      >
        {content}
      </TooltipTrigger>
      <TooltipContent>{disabledReason}</TooltipContent>
    </Tooltip>
  );
}

export { Button, buttonVariants };
